import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router';

import { makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import Alert from '@material-ui/lab/Alert';

import { Button, ErrorBoundary, Header, Loader, Modal, Nav, Notification, UnderMaintenanceProvider } from 'components';

import config from 'config';
import * as utils from 'utils';

import styles from './App.styles';

// dynamic async imports
const Blank = lazy(() => import('pages/Blank/Blank'));
const Home = lazy(() => import('pages/Home/Home'));
const Icons = lazy(() => import('pages/Icons/Icons'));
const QuoteBind = lazy(() => import('pages/QuoteBind/QuoteBind'));
const RiskDetails = lazy(() => import('pages/RiskDetails/RiskDetails'));
const QuoteBindAdmin = lazy(() => import('pages/QuoteBindAdmin/QuoteBindAdmin'));
const QuoteBindFacilityDetails = lazy(() => import('pages/QuoteBindFacilityDetails'));
const QuoteBindAggregate = lazy(() => import('pages/QuoteBindAggregate/QuoteBindAggregate'));

export const AppView = ({ user, isDev, hasLoader, sw }) => {
  const classes = makeStyles(styles, { name: 'App' })();

  // Roles
  const isBroker = utils.user.isBroker(user);
  const isUnderwriter = utils.user.isUnderwriter(user);
  const isAdmin = utils.user.isAdmin(user);
  const hasAccessToQB = utils.user.hasAccessToQB(user);

  return (
    <div id="root" className={classes.root} data-testid="root">
      <Header />

      <div id="body" className={classes.body} data-testid="body">
        <Nav />

        <div id="content" className={classes.content} data-testid="content">
          <ErrorBoundary>
            <UnderMaintenanceProvider>
              <Suspense fallback={<Loader visible />}>
                <Switch>
                  {/* Dev routes */}
                  {isDev && <Route exact path="/blank" component={Blank} />}
                  {isDev && <Route exact path="/icons" component={Icons} />}


                  {/* Quote & Bind routes */}
                  {hasAccessToQB && <Route exact path={`${config.routes.quoteBind.root}`} component={QuoteBind} />}
                  {hasAccessToQB && <Route exact path={`${config.routes.quoteBind.riskDetails}/:id?`} component={RiskDetails} />}

                  {isBroker && isAdmin && <Route exact path={`${config.routes.quoteBind.admin}`} component={QuoteBindAdmin} />}
                  {isBroker && isAdmin && (
                    <Route exact path={`${config.routes.quoteBind.facilityDetails}/:id`} component={QuoteBindFacilityDetails} />
                  )}
                  {(isBroker || isUnderwriter) && (
                    <Route exact path={`${config.routes.quoteBind.aggregate}`} component={QuoteBindAggregate} />
                  )}

                  {/* Un-protected routes */}
                  <Route exact path={`${config.routes.home.root}`} component={Home} />

                  {/* if nothing matches, redirect to home component... */}
                  <Route render={() => <Redirect to={`${config.routes.home.root}`} />} />
                </Switch>
              </Suspense>
            </UnderMaintenanceProvider>
          </ErrorBoundary>
        </div>
      </div>

      <Loader visible={hasLoader} label={utils.string.t('app.loading')} />
      <Notification />
      <Modal />
      {sw.isServiceWorkerUpdated ? (
        <Alert
          className={classes.alertRoot}
          variant="filled"
          severity="info"
          action={
            <Button icon={CheckIcon} size="small" text="Update" variant="contained" color="secondary" onClick={sw.updateServiceWorker} />
          }
        >
          {utils.string.t('app.updateApp')}
        </Alert>
      ) : null}
    </div>
  );
};

AppView.propTypes = {
  user: PropTypes.object.isRequired,
  isDev: PropTypes.bool.isRequired,
  isProduction: PropTypes.bool.isRequired,
  hasLoader: PropTypes.bool.isRequired,
  sw: PropTypes.shape({
    isServiceWorkerUpdated: PropTypes.bool,
    updateServiceWorker: PropTypes.func,
  }).isRequired,
};
